<template>
  <div class="mds-popover__wrapper mds-popover--overlay-closable mds-popover--overlay-dismissible">
    <slot name="clickTrigger" />
    <section
      :class="[sizeClass, positionClass]"
      :style="{
        visibility: isPopped ? 'visible' : 'hidden',
        opacity: isPopped ? '1' : '0',
      }"
      class="mds-popover"
      role="tooltip"
    >
      <header
        v-if="$slots.header || $slots.buttons || closable"
        class="mds-popover__header mds-popover__header--has-buttons"
      >
        <div v-if="$slots.buttons || closable" class="mds-popover__buttons">
          <slot name="buttons" />
          <button
            v-if="closable"
            class="mds-button mds-popover__button mds-button--small mds-popover__button mds-button--icon-only"
            @click="handleClose"
          >
            <mds-icon name="remove" class="mds-icon mds-button__icon mds-button__icon--right" />
          </button>
        </div>
        <h2 class="mds-popover__title">
          <slot name="header" />
        </h2>
      </header>
      <div class="mds-popover__content" @click="handleClose">
        <slot />
      </div>
    </section>
  </div>
</template>

<script>
import MdsIcon from '@mds/icon';

export default {
  name: 'pop-over',
  components: {
    MdsIcon,
  },
  props: {
    popped: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: '300px',
      validator(size) {
        return ['200px', '300px', '500px'].some((allowedSize) => allowedSize === size);
      },
    },
    closable: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: '',
      validator(position) {
        return [
          'top-center',
          'top-right',
          'top-left',
          'bottom-center',
          'bottom-right',
          'bottom-left',
          'right-center',
          'right-top',
          'right-bottom',
          'left-center',
          'left-top',
          'left-bottom',
        ].some((allowedPosition) => allowedPosition === position);
      },
    },
  },
  data() {
    return {
      isPopped: this.popped,
    };
  },
  computed: {
    sizeClass() {
      const sizeCssMap = {
        '200px': 'mds-popover--width-200px',
        '300px': 'mds-popover--width-300px',
        '500px': 'mds-popover--width-500px',
      };
      return sizeCssMap[this.size] || 'mds-popover--width-300px';
    },
    positionClass() {
      const positionCssMap = {
        'top-center': 'mds-popover--top-center',
        'top-right': 'mds-popover--top-right',
        'top-left': 'mds-popover--top-left',
        'bottom-center': 'mds-popover--bottom-center',
        'bottom-right': 'mds-popover--bottom-right',
        'bottom-left': 'mds-popover--bottom-left',
        'right-center': 'mds-popover--right-center',
        'right-top': 'mds-popover--right-top',
        'right-bottom': 'mds-popover--right-bottom',
        'left-center': 'mds-popover--left-center',
        'left-top': 'mds-popover--left-top',
        'left-bottom': 'mds-popover--left-bottom',
      };
      return positionCssMap[this.position] || 'mds-popover--bottom-left';
    },
  },
  watch: {
    popped(popped) {
      this.isPopped = popped;
    },
    isPopped(isPopped) {
      this.removeDismissEvents();
      if (isPopped) {
        this.attachDismissEvents();
      }
    },
  },
  mounted() {
    this.attachClickTriggerEvent();
  },
  beforeDestroy() {
    this.removeClickTriggerEvents();
    this.removeDismissEvents();
  },
  methods: {
    pop(isPopped) {
      this.isPopped = isPopped;
      this.$emit('change', isPopped);
    },
    handleClose() {
      this.isPopped = false;
      this.$emit('closed');
    },
    handleClickTriggerEvent(ev) {
      ev.preventDefault();
      this.pop(!this.isPopped);
    },
    handlePressEscapeEvent(ev) {
      const key = ev.key && ev.key.toLowerCase();
      if (key === 'escape' || ev.code === '27' || ev.keyCode === 27) {
        this.pop(false);
      }
    },
    handleClickOutsideEvent(ev) {
      const clickedOutsidePopover = this.$el !== ev.target && !this.$el.contains(ev.target);
      if (clickedOutsidePopover) {
        this.pop(false);
      }
    },
    attachDismissEvents() {
      window.document.addEventListener('keydown', this.handlePressEscapeEvent, { once: true });
      window.document.addEventListener('mousedown', this.handleClickOutsideEvent);
    },
    attachClickTriggerEvent() {
      if (Array.isArray(this.$slots.clickTrigger)) {
        this.$slots.clickTrigger.forEach((t) => t.elm.addEventListener('click', this.handleClickTriggerEvent));
      }
    },
    removeDismissEvents() {
      window.document.removeEventListener('keydown', this.handlePressEscapeEvent);
      window.document.removeEventListener('mousedown', this.handleClickOutsideEvent);
    },
    removeClickTriggerEvents() {
      if (Array.isArray(this.$slots.clickTrigger)) {
        this.$slots.clickTrigger.forEach((t) => t.elm.removeEventListener('click', this.handleClickTriggerEvent));
      }
    },
  },
};
</script>
